import { useState, useEffect } from 'react';
import { Store, ShoppingCart, Sparkles, Receipt, Wifi, Info } from 'lucide-react';

export default function CustomerDisplay() {
  const [data, setData] = useState({
    cart: [],
    total: 0,
    subtotal: 0,
    tax: 0,
    discount: 0,
    promos: [],
    currency: '$'
  });

  useEffect(() => {
    const channel = new BroadcastChannel('customer_display');
    channel.onmessage = (event) => {
      setData(event.data);
    };
    return () => channel.close();
  }, []);

  return (
    <div className="h-screen bg-[#0f172a] text-white flex flex-col overflow-hidden font-sans selection:bg-brand/30">
      {/* High-End Header */}
      <header className="h-24 border-b border-white/5 bg-white/5 backdrop-blur-xl flex items-center justify-between px-12 z-10 shadow-2xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
            <Store size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tighter uppercase">Nexus<span className="text-indigo-500">POS</span></h1>
            <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em]">Customer Experience Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-8">
           <div className="text-right">
             <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">Status</p>
             <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase">
               <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /> Linked to Terminal
             </div>
           </div>
        </div>
      </header>

      <main className="flex-1 flex overflow-hidden">
        {/* Left: Dynamic Cart Display */}
        <div className="flex-1 p-12 overflow-y-auto custom-scrollbar bg-gradient-to-br from-transparent to-white/[0.02]">
          <div className="max-w-4xl mx-auto space-y-6">
            <h2 className="text-4xl font-black tracking-tight mb-12 flex items-center gap-4">
               <ShoppingCart size={40} className="text-indigo-500" /> 
               Your Order
            </h2>

            {data.cart.length === 0 ? (
              <div className="h-[50vh] flex flex-col items-center justify-center text-center opacity-30">
                <div className="w-32 h-32 bg-white/5 rounded-full flex items-center justify-center mb-8 border border-white/10">
                  <Receipt size={64} />
                </div>
                <h3 className="text-3xl font-black uppercase tracking-tight mb-2">Welcome!</h3>
                <p className="text-xl font-medium max-w-md">Items you purchase will appear here in real-time.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.cart.map((item, i) => (
                  <div key={i} className="flex items-center gap-6 p-6 bg-white/[0.03] border border-white/10 rounded-[2.5rem] animate-fade-in-up hover:bg-white/[0.05] transition-all">
                    <div className="w-16 h-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-2xl font-black text-indigo-400 border border-indigo-500/20">
                      {item.name[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-black tracking-tight leading-tight mb-1">{item.name}</h4>
                      <div className="flex items-center gap-4">
                        <span className="text-white/40 font-black text-sm uppercase tracking-widest">Qty: {item.quantity}</span>
                        <span className="w-1 h-1 bg-white/20 rounded-full" />
                        <span className="text-white/40 font-black text-sm uppercase tracking-widest">{data.currency}{item.price.toFixed(2)} / unit</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black text-white">{data.currency}{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Summary Panel & Ads */}
        <div className="w-[450px] border-l border-white/5 bg-[#1e293b]/50 backdrop-blur-3xl flex flex-col p-12">
          <div className="flex-1">
            <h3 className="text-xs font-black text-white/40 uppercase tracking-[0.3em] mb-8">Current Totals</h3>
            
            <div className="space-y-6">
              <div className="flex justify-between items-center group">
                <span className="text-white/60 font-bold text-xl">Subtotal</span>
                <span className="text-2xl font-black">{data.currency}{data.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-white/40">
                <span className="text-lg font-bold">Tax</span>
                <span className="text-xl font-black">{data.currency}{data.tax.toFixed(2)}</span>
              </div>
              
              {data.discount > 0 && (
                <div className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-3xl space-y-3 animate-pulse shadow-2xl shadow-emerald-500/10">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black uppercase text-emerald-400 flex items-center gap-2 tracking-[0.2em]">
                      <Sparkles size={14} /> Total Discount
                    </span>
                    <span className="text-2xl font-black text-emerald-400">-{data.currency}{data.discount.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.promos.map(p => (
                      <span key={p.id} className="text-[10px] font-black bg-emerald-500 text-white px-3 py-1 rounded-full uppercase tracking-tighter shadow-lg">{p.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-12 pt-12 border-t border-white/10">
              <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] mb-2">Grand Total Due</p>
              <h2 className="text-8xl font-black tracking-tighter text-indigo-400 drop-shadow-2xl">
                {data.currency}{data.total.toFixed(2)}
              </h2>
            </div>
          </div>

          {/* Ad Section / Promotion */}
          <div className="mt-auto">
            <div className="p-8 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-[3rem] shadow-2xl shadow-indigo-500/20 relative overflow-hidden group">
               <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700" />
               <h4 className="text-sm font-black uppercase tracking-[0.2em] mb-2 text-indigo-100">Special Offer</h4>
               <p className="text-2xl font-black leading-tight mb-4 tracking-tight">Join Nexus Rewards to earn 10% back on this order!</p>
               <button className="bg-white text-indigo-600 font-black text-[10px] uppercase tracking-widest px-6 py-3 rounded-2xl shadow-xl hover:-translate-y-1 transition-all active:scale-95">
                 Sign Up Now
               </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer / Ticker */}
      <footer className="h-16 bg-white/[0.02] border-t border-white/5 flex items-center px-12 text-[10px] font-black text-white/40 uppercase tracking-[0.4em]">
        <div className="flex items-center gap-12 overflow-hidden whitespace-nowrap">
          <span className="flex items-center gap-3"><Wifi size={14} className="text-emerald-500" /> Secure Encryption Active</span>
          <span className="w-1.5 h-1.5 bg-white/20 rounded-full" />
          <span>Next-Gen Point of Sale Experience</span>
          <span className="w-1.5 h-1.5 bg-white/20 rounded-full" />
          <span>Powered by Midnight Nexus Pro</span>
        </div>
      </footer>
    </div>
  );
}
